import React, { useCallback, useState } from "react";
import { clearOralQuery, clearQuery, setOralQuery, setQuery } from "../../../../services/local-session-storage";
import { addPatient } from "../../../../services/decisionFlow";
import QueryForm from "./form/query-form";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { AddPatient, IPatientModalValues } from "./add-patient";
import { AddOutlined } from "@mui/icons-material";
import { AppModal } from "../../../../components/ui";
import { updatePatientObservation } from "../../../../services/patient.service";
import { useLocation, useNavigate } from "react-router-dom";
import { SimpleSnackbar } from "../../../../components/snackbar";
import { isPatientManagementEnabled } from "../../../../services/feature-flags.service";
import { WizardShell } from "../../../../components/ui";
import { TOPIC_STEPS } from "../wizard-steps";

type Props = {
    onFinish: () => void
    onCancel: () => void
}

export const Patient: React.FC<Props> = ({ onFinish, onCancel }) => {
    const [open, setOpen] = useState(false)
    const [patientIds, setPatientIds] = useState<number[]>([])
    const [reloadPatient, setReloadPatient] = useState(false)
    const [openRegistrationChoice, setOpenRegistrationChoice] = useState(false)
    const [showSnackbar, setShowSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const navigate = useNavigate()
    const location = useLocation()
    const preselectId = (location.state as { patientId?: number } | null)?.patientId
    const theme = useTheme()

    const handleAdd = () => {
        if (isPatientManagementEnabled()) {
            setOpenRegistrationChoice(true)
        } else {
            setOpen(true)
        }
    }

    const handleSimpleRegistration = () => {
        setOpenRegistrationChoice(false)
        setOpen(true)
    }

    const handleCompleteRegistration = () => {
        setOpenRegistrationChoice(false)
        navigate('/dashboard/patients')
    }

    const handleInitiate = (patients?: number[]) => {
        clearQuery()
        clearOralQuery()

        const selectedPatients = !!patients ? patients : patientIds

        if (selectedPatients.length > 0) {
            if (selectedPatients.length === 1) {
                setOralQuery({
                    patient: selectedPatients[0]
                })
                setQuery({
                    canEdit: true,
                    patientId: selectedPatients[0],
                })
            } else {
                setOralQuery({
                    patientIds: selectedPatients
                })
                setQuery({
                    canEdit: true,
                    patientIds: selectedPatients,
                })
            }
        } else {
            setQuery({
                canEdit: true,
            })
        }

        onFinish()
    }

    const handlePatient = useCallback(async (patient?: IPatientModalValues) => {
        setOpen(false)
        if (!patient) return

        const hasSelectedPatientId = patient.id !== undefined && patient.id !== null && !Number.isNaN(Number(patient.id))

        if (hasSelectedPatientId) {
            await updatePatientObservation(Number(patient.id), patient.observations)
            setReloadPatient((prev) => !prev)
            setSnackbarMessage('Paciente atualizado com sucesso! Selecione o paciente e clique em iniciar consulta.')
            setShowSnackbar(true)
        } else {
            const result = await addPatient({
                name: patient.patientName,
                observation: patient.observations,
            })
            setReloadPatient((prev) => !prev)
            setSnackbarMessage('Paciente cadastrado com sucesso! Agora selecione o paciente para iniciar a consulta.')
            setShowSnackbar(true)
        }
    }, [setOpen, setReloadPatient])

    return (
        <>
            <SimpleSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                onDismiss={() => setShowSnackbar(false)}
            />

            <AppModal
                open={openRegistrationChoice}
                onClose={() => setOpenRegistrationChoice(false)}
                kicker="Paciente"
                title="Tipo de cadastro"
                maxWidth={460}
                footer={
                    <>
                        <Button
                            onClick={handleSimpleRegistration}
                            sx={{ height: 46, px: 3, fontWeight: 600, color: "primary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.primary.main}` }}
                        >
                            Cadastro simples
                        </Button>
                        <Button onClick={handleCompleteRegistration} variant="contained" sx={{ height: 46, px: 3 }}>
                            Cadastro completo
                        </Button>
                    </>
                }
            >
                <Typography sx={{ fontSize: "14.5px", color: "text.secondary", lineHeight: 1.6 }}>
                    Você gostaria de fazer o cadastro completo do paciente?
                </Typography>
            </AppModal>

            <AddPatient open={open} onClose={handlePatient} reloadPatients={reloadPatient} patientSelected={setPatientIds} />

            <WizardShell
                steps={TOPIC_STEPS as unknown as string[]}
                activeIndex={0}
                onCancel={onCancel}
                onNext={() => handleInitiate()}
                nextLabel="Continuar"
            >
                <Typography
                    sx={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: 24,
                        fontWeight: 400,
                        color: "text.primary",
                        mb: "4px",
                    }}
                >
                    Para qual paciente?
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: "20px" }}>
                    Selecione um ou mais pacientes para vincular a consulta ao histórico deles.
                </Typography>

                <Button
                    onClick={handleAdd}
                    startIcon={<AddOutlined sx={{ fontSize: 18 }} />}
                    fullWidth
                    sx={{
                        mt: "16px",
                        height: 48,
                        border: `1.5px dashed ${theme.palette.divider}`,
                        borderRadius: "10px",
                        color: "text.secondary",
                        backgroundColor: "transparent",
                        "&:hover": {
                            backgroundColor: theme.palette.brand.cream,
                            borderColor: theme.palette.primary.main,
                            color: "primary.main",
                        },
                    }}
                >
                    Cadastrar novo paciente
                </Button>

                <Box sx={{ mt: "16px" }}>
                    <QueryForm reloadPatients={reloadPatient} patientSelected={setPatientIds} preselectId={preselectId} />
                </Box>
            </WizardShell>
        </>
    )
}
